import React, { useState, useRef, useEffect } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import MapScreen from './MapScreen';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const MAPS_LIBRARIES = ['places'];

function MapScreenWrapper({ sharedRestroomId }) {
  const [shouldLoadMaps, setShouldLoadMaps] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const containerRef = useRef(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: MAPS_LIBRARIES,
    version: 'weekly',
    // Only load when shouldLoadMaps is true
    loading: 'async',
  });

  // Intersection Observer to load maps when component is visible
  useEffect(() => {
    if (!containerRef.current || userInteracted) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShouldLoadMaps(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [userInteracted]);

  // Fallback: load on user interaction
  const handleInteraction = () => {
    if (!shouldLoadMaps) {
      setUserInteracted(true);
      setShouldLoadMaps(true);
    }
  };

  // Always load maps after a short delay as fallback
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!shouldLoadMaps) {
        setShouldLoadMaps(true);
      }
    }, 3000); // 3 second fallback

    return () => clearTimeout(timeout);
  }, [shouldLoadMaps]);

  if (loadError) {
    return (
      <div className="map-error-screen" onClick={handleInteraction}>
        <div className="error-content">
          <h2>🗺️ Map Error</h2>
          <p>Failed to load Google Maps. Click to retry.</p>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div 
        ref={containerRef}
        className="map-loading-screen"
        onClick={handleInteraction}
      >
        <div className="loading-content">
          <img src="/app-icon.png" alt="The Crapper Mapper" className="loading-logo" />
          <p>Loading Map...</p>
          <button className="btn btn-secondary" onClick={handleInteraction}>
            Tap to Load Map
          </button>
        </div>
      </div>
    );
  }

  return <MapScreen sharedRestroomId={sharedRestroomId} />;
}

export default MapScreenWrapper;
